import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import makeStyles from '@mui/styles/makeStyles';
import {Box} from '@mui/material';
import clsx from 'clsx';
import moment from 'moment';
import config from '../../../../app/config';
import API from '../../../../app/api';
import {useDispatch, useSelector} from 'react-redux';
import {handleSubmitBedRequest} from '../../../../store/reducers/bedManagement';
import useOpenState from '../../../../hooks/useOpenState';
import MessageBox from '../../../../util/messageBox';
import {setSnackbar} from '../../../../store/reducers/globals';
import Typography from '@mui/material/Typography';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import Formatter from '../../../../util/formatter';
import {user} from '../../../../store/reducers/user';
import useCliniciansAndNursesDict from '../../../../hooks/useCliniciansAndNursesDict';
import AgyleLoadingSpinner from '../../../../components/other/agyleLoadingSpinner';
import {getWardsIdForPatientsSearch} from '../../../../store/reducers/wardMap';
import {multiHospitalMode} from '../../../../store/reducers/ward';
import {openPatientForm} from '../../../../store/reducers/patientContext';

const useButtonStyles = makeStyles((theme) => {
    return ({
        background: {
            backgroundColor: theme.palette.background.paperDarker,
            border: `1px solid transparent`,
            borderRadius: 10,
            boxShadow: `0px ${theme.palette.mode === 'light' ? 4 : 0}px ${theme.palette.mode === 'light' ? 4 : 0}px 0px rgba(0, 0, 0, 0.25)`,
            height: 100,
            padding: 1.5,
            '&:hover': {
                borderColor: theme.palette.acuityColors.color5.background,
            },
            '&.padding': {
                padding: 8,
                height: 'auto',
            },
        },
        buttonHeader: {
            display: 'flex',
            backgroundColor: theme.palette.background.paperDarker,
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            paddingBottom: 11,
            fontWeight: 'bold',
            fontSize: '0.875rem',
            paddingTop: 7,
            paddingLeft: 12,
        },
        button: {
            backgroundColor: theme.palette.background.paperDarker,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
            padding: '0 8px',
        },
        filledButtonHeader: {
            backgroundColor: theme.palette.mainScreenButton.headerFilled,
            paddingLeft: 10,
            paddingRight: 10,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            height: 40,
        },
        buttonHeaderTitle: {
            fontWeight: 'bold',
            fontSize: '1rem',
        },
        right: {
            marginLeft: 'auto',
            gap: 10,
        },
        divRow: {
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'row',
            alignItems: 'center',
        },
        cancelRequest: {
            backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#494b4c',
            borderRadius: 4,
            padding: '4px 6px',
            fontSize: '0.875rem',
            color: '#e25b61',
            fontWeight: 'bold',
            '&:hover': {
                backgroundColor: '#e25b61',
                color: '#fff',
                cursor: 'pointer',
            },
        },
        body: {
            flexWrap: 'wrap',
            padding: 10,
            backgroundColor: theme.palette.mainScreenButton.formFilledDetails,
            textTransform: 'none !important',
            maxHeight: 'min-content',
            height: '100%',
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
        },
        bold: {
            fontWeight: 'bold',
        },
        ack: {
            fontWeight: 'normal',
        },
        status: {
            marginTop: 5,
            borderRadius: 4,
            padding: 5,
            textAlign: 'center',
            fontWeight: 'bold',
            '&.red': {
                backgroundColor: '#e25b61',
            },
            '&.amber': {
                backgroundColor: '#f7a202',
            },
            '&.green': {
                backgroundColor: '#4EAC6F',
            },
            '&.empty': {
                border: `solid 1px ${theme.palette.mode === 'light' ? '#494b4c' : '#eff1f3'}`,
            },
        },
        small: {
            fontSize: '0.875rem',
        },
    });
});


/**
 * The Button can only be used for bed request from Bed Management module
 */
const BedRequestButton = ({patient, disabled}) => {
    const classes = useButtonStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // User
    const userObject = useSelector(user);
    const fullName = Formatter.formatUserFullName(userObject);

    // Selectors
    const isMultiHospitalMode = useSelector(multiHospitalMode);

    const [bedRequest, setBedRequest] = useState(null);
    const [status, setStatus] = useState();

    // Bed status
    const [bedStatus, setBedStatus] = useState();
    const [bedStatusLoading, setBedStatusLoading] = useState(false);

    // Cancel bed request
    const [isCancelOpen, openCancel, closeCancel] = useOpenState(false, true);
    const [reason, setReason] = useState('');
    const [nursesCliniciansList] = useCliniciansAndNursesDict('', !config?.dropdownsWithCliniciansAndNurses);

    useEffect(() => {
        if (patient)
            setBedRequest(_.find(patient?.bedRequests, br => !br?.followUp && br?.status === 'NEW' && br?.requested?.checked) || null);
    }, [patient]);

    useEffect(() => {
        if (bedRequest) {
            let requestStatus = {
                label: 'Requested',
                secondLabel: null,
                class: 'red',
                filed: 'requested',
            };
            if (bedRequest?.canceledAcknowledge?.checked) {
                requestStatus = {
                    label: 'Cancelled',
                    secondLabel: ` (Acknowledged ${moment(bedRequest?.canceledAcknowledge.when).format(config.defaultDateTimeFormat)})`,
                    class: 'empty',
                    filed: 'canceled',
                };
            } else if (bedRequest?.canceled?.checked) {
                requestStatus = {
                    label: 'Cancelled',
                    secondLabel: null,
                    class: 'empty',
                    filed: 'canceled',
                };
            } else if (bedRequest?.patientReady?.checked) {
                requestStatus = {
                    label: bedRequest?.allocated?.checked ? 'Allocated' : 'Requested',
                    secondLabel: ' Patient Ready',
                    class: bedRequest?.allocated?.checked ? 'green' : 'amber',
                    filed: 'patientReady',
                };
            } else if (bedRequest?.allocated?.checked) {
                requestStatus = {
                    label: 'Allocated',
                    secondLabel: ' Patient Not Ready',
                    class: 'amber',
                    filed: 'allocated',
                };
            } else if (bedRequest?.requestedAcknowledge?.checked) {
                requestStatus = {
                    label: 'Requested',
                    secondLabel: ` (Acknowledged ${moment(bedRequest?.requestedAcknowledge.when).format(config.defaultDateTimeFormat)})`,
                    class: 'amber',
                    filed: 'requested',
                };
            }
            setStatus(requestStatus);
        }
    }, [bedRequest]);

    // Check Bed Ready Status
    useEffect(() => {
        let interval;
        setBedStatusLoading(true);
        if (!bedRequest?.canceled?.checked && bedRequest?.allocated?.checked && bedRequest?.destination?.bedId) {
            checkBedReady();
            interval = setInterval(checkBedReady, 10000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [bedRequest]);

    const checkBedReady = async () => {
        const wardId = bedRequest.destination.wardId;
        const bedId = bedRequest.destination.bedId;
        const hospitalId = bedRequest.destination.hospitalId;
        let isOccupied = false;
        let haveStatus = false;
        let requestStatus = {
            label: 'Bed Not Ready',
            class: 'red',
        };

        await API.checkBedOccupied(wardId, bedId)
            .then(r => isOccupied = r);
        if (!isOccupied) {
            let wardsId = wardId;
            if (isMultiHospitalMode) {
                wardsId =  dispatch(getWardsIdForPatientsSearch([wardsId], hospitalId));
            }
            await API.getWardData(wardsId)
                .then((res) => {
                    if (res?.bedStatuses) {
                        haveStatus = !!res?.bedStatuses?.[bedId];
                    }
                })
                .catch(err => {
                    console.error(err);
                    dispatch(setSnackbar({
                        type: 'error',
                        message: `There was a problem getting beds status for ward id ${wardsId}`,
                    }));
                });
        }

        if (isOccupied) {
            requestStatus = {
                label: 'Bed Occupied',
                class: 'amber',
            };
        } else if (!haveStatus) {
            requestStatus = {
                label: 'Bed Ready',
                class: 'green',
            };
        }
        setBedStatus(requestStatus);
        setBedStatusLoading(false);
    };

    const changeUser = (state) => {
        const consultant = _.find(nursesCliniciansList, {userName: userObject.userName});

        return ({
            ...state,
            user: fullName,
            who: consultant,
            login: userObject.userName,
            timestamp: moment().toISOString(),
        });
    };

    const handleCloseSubmitBedRequest = () => {
        if (!_.isEmpty(reason)) {
            let clone = _.cloneDeep(bedRequest);
            clone.canceled = {
                ...clone?.canceled,
                ...changeUser(clone?.canceled),
                note: reason,
                checked: true,
                when: moment().toISOString(),
            };

            dispatch(handleSubmitBedRequest(patient, clone, () => handleCloseCancelBedRequest()));
        } else {
            dispatch(setSnackbar({type: 'warning', message: 'Please provide a reason.'}));
        }
    };

    const handleCloseCancelBedRequest = () => {
        closeCancel();
        setReason('');
    };

    const handleOpenForm = (e) => {
        if (!disabled) {
            const disableOpenPatient = e.target.closest('.disabled-options') !== null;
            if (!disableOpenPatient) dispatch(openPatientForm('newBedRequest'));
        }
    };

    const getLastAction = (type) => {
        const field = bedRequest[status?.filed];
        if (field) {
            return type === 'when' ? moment(field[type]).format(config.defaultDateTimeFormat) : field[type];
        }
        return '-';
    };

    const renderStatusButton = () => {
        const showBedStatus = !bedRequest?.canceled?.checked && bedRequest?.allocated?.checked && bedRequest?.destination?.bedId;
        return (
            <div
                className={clsx(classes.background, 'padding')}
                onClick={handleOpenForm}
            >
                <div className={clsx(classes.filledButtonHeader, classes.divRow)}>
                    <span className={classes.buttonHeaderTitle}>
                        {t('Bed Request')}
                    </span>

                    <div className={clsx(classes.right, classes.divRow)}>
                        {!bedRequest?.canceled?.checked &&
                            <div
                                onClick={openCancel}
                                className={clsx('disabled-options', classes.cancelRequest)}
                            >
                                {t('Cancel Request')}
                            </div>
                        }

                        <span style={{fontSize: '0.875rem'}}>
                            {t('update')}
                        </span>
                    </div>
                </div>

                <div className={clsx(classes.divRow, classes.body)}>
                    <div style={{flexBasis: '50%'}}>
                        <div className={classes.bold}>
                            {t('Date')}:
                        </div>

                        <div className={classes.small}>
                            {getLastAction('when')}
                        </div>
                    </div>

                    <div style={{flexBasis: '50%'}}>
                        <div className={classes.bold}>
                            {t('By')}:
                        </div>

                        <div className={classes.small}>
                            {getLastAction('user')}
                        </div>
                    </div>

                    <div style={{flexBasis: '100%', marginTop: 20, marginBottom: !showBedStatus ? 5 : 0}}>
                        <div className={classes.bold} style={{marginBottom: 10}}>
                            {t('Status')}:
                        </div>

                        <div className={clsx(classes.status, status?.class)}>
                            {status?.label}
                            {status?.secondLabel &&
                                <span className={classes.ack}>
                                    {status.secondLabel}
                                </span>
                            }
                        </div>
                    </div>

                    {showBedStatus &&
                        <div style={{flexBasis: '100%', marginTop: 7}}>
                            {bedStatusLoading && <AgyleLoadingSpinner disableText spinnerSize={14}/>}

                            {!bedStatusLoading &&
                                <div className={clsx(classes.status, bedStatus?.class)}>
                                    {bedStatus?.label || 'UNK'}
                                </div>
                            }
                        </div>
                    }

                    {(bedRequest?.allocated?.checked && bedRequest?.destination?.wardId) &&
                        <>
                            <div style={{flexBasis: '50%', marginTop: 20, marginBottom: 10}}>
                                <div className={classes.bold}>
                                    {t('Ward')}:
                                </div>

                                <div className={classes.small}>
                                    {bedRequest?.destination?.wardName || '-'}
                                </div>
                            </div>

                            <div style={{flexBasis: '50%', marginTop: 20, marginBottom: 10}}>
                                <div className={classes.bold}>
                                    {t('Zone/Bed')}:
                                </div>

                                <div className={classes.small}>
                                    {bedRequest?.destination?.bayName || '-'} / {bedRequest?.destination?.bedName || '-'}
                                </div>
                            </div>
                        </>
                    }
                </div>
            </div>
        );
    };

    const renderEmptyButton = () => {
        return (
            <Box
                onClick={handleOpenForm}
                className={classes.background}
                style={{
                    cursor: disabled ? 'default' : 'pointer',
                }}
            >
                <div className={classes.buttonHeader}>
                    {t('Bed Request')}
                </div>

                <div className={classes.button}>
                    <AgyleButton
                        style={{marginTop: 10, color: '#2280f7', borderColor: '#2280f7'}}
                        width="99%" variant="outlined" color="primary"
                        startIcon={<AddIcon/>}
                        disabled={disabled}
                        onClick={handleOpenForm}
                    >
                        Add Data
                    </AgyleButton>
                </div>
            </Box>
        );
    };

    const renderCancelOption = () => {

        return (
            <>
                <Typography variant="subtitle2" style={{fontWeight: 600, marginBottom: 5, marginTop: 10}}>
                    {t('Reason')}
                </Typography>

                <AgyleTextField
                    fullWidth
                    value={reason}
                    placeholder={t('Specify the reason of cancellation')}
                    onChange={e => setReason(e.target.value)}
                />
            </>
        );
    };

    const getRowHeight = () => {
        if (bedRequest) {
            return (bedRequest?.allocated?.checked && bedRequest?.destination?.wardId) ? 3 : 2;
        }
        return 1;
    };

    return (
        <>
            <div style={{gridRow: `${getRowHeight()} span`}}>
                {!bedRequest && renderEmptyButton()}
                {bedRequest && renderStatusButton()}
            </div>

            <MessageBox
                open={isCancelOpen}
                title={t('Cancel Bed Request')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                fullWidth
                onAccept={handleCloseSubmitBedRequest}
                onClose={handleCloseCancelBedRequest}
                messageObject={renderCancelOption()}
            />
        </>
    );
};

export default BedRequestButton;